import { defineQuery, enterQuery, exitQuery } from "bitecs";
import { Rigidbody, SnapFrame } from "../bit-components";
import { HeldRemoteLeft, HeldRemoteRight, Networked } from "../bit-components";

import { Matrix4, Quaternion, Vector3 } from "three";
import { setMatrixWorld } from "../utils/three-utils";
import { anyEntityWith } from "../utils/bit-utils";
import { takeSoftOwnership } from "../utils/take-soft-ownership";

// let isLeftClickPressed = false;
// let isLeftClickPressedAutoSnap = false;

// // Track mouse state natively
// window.addEventListener('mousedown', (event) => {
//     if (event.button === 0) { // 0 is the left mouse button
//         isLeftClickPressed = true;
//         isLeftClickPressedAutoSnap = true;
//     }
// });

// window.addEventListener('mouseup', (event) => {
//     if (event.button === 0) { // 0 is the left mouse button
//         setTimeout(() => {
//             isLeftClickPressed = false;
//         }, 50)
//         setTimeout(() => {
//             isLeftClickPressedAutoSnap = false;
//         }, 50)
//     }
// });

let releaseTimeout = null;
let lastHeldObject = null;

const mediaFramesQuery = defineQuery([SnapFrame]);
const takeOwnershipOnTimeout = new Map();
// Centralized registry to track captured eids and their objeids
const capturedEids = new Map(); // Map<eid, objeid>

// Map to track which frame has captured which eid
// Map to track which frame has captured which eids
const frameToEids = new Map(); // Map<frameEid, Set<eid>>

export function snapWallSystem(world, physicsSystem) {
  const enteredMediaFramesQuery = enterQuery(mediaFramesQuery);
  enteredMediaFramesQuery(world).forEach(eid => {
    if (Networked.owner[eid] === APP.getSid("reticulum")) {
      takeOwnershipOnTimeout.set(
        eid,
        setTimeout(() => {
          if (Networked.owner[eid] === APP.getSid("reticulum")) {
            takeSoftOwnership(world, eid);
          }
          takeOwnershipOnTimeout.delete(eid);
        }, 10000)
      );
    }

    const guideObj = world.eid2obj.get(SnapFrame.guide[eid]);
    guideObj.visible = false;

    const frameObj = world.eid2obj.get(eid);
    frameObj.add(guideObj);
  });

  const exitedMediaFramesQuery = exitQuery(mediaFramesQuery);

  exitedMediaFramesQuery(world).forEach(eid => {
    const timeout = takeOwnershipOnTimeout.get(eid);
    if (timeout) {
      clearTimeout(timeout);
      takeOwnershipOnTimeout.delete(eid);
    }
  });

  function isCaptured(eid) {
    return capturedEids.has(eid);
  }

  const snapToFrame = (() => {
    const localPos = new Vector3();
    const targetPos = new Vector3();
    const targetQuat = new Quaternion();
    const targetScale = new Vector3();
    const m4 = new Matrix4();

    // Offset to prevent image flickering with the wall (Z-fighting).
    // If the image ends up "inside" the wall, change this value to positive or negative.
    const SURFACE_OFFSET = 0.01;

    return (world, frame, target) => {
      const frameObj = world.eid2obj.get(frame);
      let refGeometry = null;

      // 1. Find geometry (the thick guide box)
      frameObj.traverse(function (child) {
        if (child.geometry && !refGeometry) {
          refGeometry = child.geometry;
          if (!refGeometry.boundingBox) refGeometry.computeBoundingBox();
        }
      });

      if (!refGeometry && frameObj.geometry) {
        refGeometry = frameObj.geometry;
        if (!refGeometry.boundingBox) refGeometry.computeBoundingBox();
      }

      if (!refGeometry) return;

      const targetObj = world.eid2obj.get(target);

      // 2. Get transformations
      targetObj.updateMatrixWorld();
      targetObj.matrixWorld.decompose(targetPos, targetQuat, targetScale);
      frameObj.updateMatrixWorld();

      // 3. Convert to wall's local space
      localPos.copy(targetPos);
      frameObj.worldToLocal(localPos);

      // ---------------------------------------------------------
      // MAIN CHANGE:
      // Removed face detection logic.
      // Always force to "min.z" limit (the back face).
      // ---------------------------------------------------------

      // Note: If you see it snapping to the wrong face (the front),
      // change 'min.z' to 'max.z' in the following line.
      const backFaceZ = refGeometry.boundingBox.min.z;

      // Adjust depth so it sticks to that face.
      // Depending on which direction your wall normals point,
      // you may need to add (+) or subtract (-) the SURFACE_OFFSET.
      // Here we assume we want to go slightly "outward" (toward max) or "backward" (toward min).
      // Try subtraction (-) first, if it gets buried, use addition (+).
      localPos.z = backFaceZ + SURFACE_OFFSET;

      // 4. Clamp X and Y (keep within the green box edges)
      const width = refGeometry.boundingBox.max.x - refGeometry.boundingBox.min.x;
      const height = refGeometry.boundingBox.max.y - refGeometry.boundingBox.min.y;

      const halfWidth = width / 2;
      const halfHeight = height / 2;
      const margin = 0.0;

      localPos.x = Math.max(-halfWidth + margin, Math.min(halfWidth - margin, localPos.x));
      localPos.y = Math.max(-halfHeight + margin, Math.min(halfHeight - margin, localPos.y));

      // 5. Apply changes
      frameObj.localToWorld(localPos);

      const frameRot = new Quaternion();
      frameObj.matrixWorld.decompose(new Vector3(), frameRot, new Vector3());

      setMatrixWorld(targetObj, m4.compose(localPos, frameRot, targetObj.scale));
    };
  })();
  function display(world, frame, grabbing) {
    const frameObj = world.eid2obj.get(frame);
    frameObj.children[0].visible = !!grabbing;
  }

  const objectsIds = window.APP.scene.systems["listed-media"].els.map(el => el.eid);

  function getCapturableEntities(world, physicsSystem, frame) {
    const capturables = new Set();
    try {
      const collisions = physicsSystem.getCollisions(Rigidbody.bodyId[frame]);

      for (let i = 0; i < collisions.length; i++) {
        const bodyData = physicsSystem.bodyUuidToData.get(collisions[i]);
        const eid = bodyData.object3D.eid;

        // Only capture entities whose eid is part of objectsIds
        if (objectsIds.includes(eid)) {
          capturables.add(eid);
        }
      }
    } catch (e) {
      console.error(`Error in getCapturableEntities: ${e}`);
    }

    return capturables;
  }

  const mediaFrames = mediaFramesQuery(world);

  for (let i = 0; i < mediaFrames.length; i++) {
    let objeid = anyEntityWith(APP.world, HeldRemoteLeft) || anyEntityWith(APP.world, HeldRemoteRight);
    const eid2obj = window.APP.world.eid2obj.get(objeid);

    if (eid2obj && eid2obj.name === "Camera Tool") {
      objeid = null;
    }

    if (eid2obj && eid2obj.el && eid2obj.el.classList.contains("pen")) {
      objeid = null;
    }

    if (objeid) {
      lastHeldObject = objeid;
      const pinnable = window.APP.world.eid2obj.get(lastHeldObject).el?.components["pinnable"]?.data.pinned;
      if (pinnable) {
        lastHeldObject = null;
      }
    }

    const frame = mediaFrames[i];
    const capturedEidSet = frameToEids.get(frame) || new Set();

    // Iterate over a copy to allow modifications during iteration
    const capturedEidsArray = Array.from(capturedEidSet);

    capturedEidsArray.forEach(capturedEid => {
      const isCapturedColliding = getCapturableEntities(world, physicsSystem, frame).has(capturedEid);
      if (isCapturedColliding && lastHeldObject === capturedEid) {
        // Object is still colliding with the wall, keep it snapped
        snapToFrame(world, frame, capturedEid);
      } else {
        // Object is no longer colliding with the wall, release it
        // world.eid2obj.get(capturedEid).el.setAttribute("floaty-object", {modifyGravityOnRelease: true})
        capturedEids.delete(capturedEid);
        capturedEidSet.delete(capturedEid);
      }
    });

    // Automatically detect objects near the frame, but only for objects in objectsIds
    const capturables = getCapturableEntities(world, physicsSystem, frame);

    if (capturables.size > 0) {
      capturables.forEach(capturable => {
        if (!isCaptured(capturable)) {
          // Lock the eid by adding it to the centralized registry
          capturedEids.set(capturable, frame);

          // Add to frameToEids
          const eidSet = frameToEids.get(frame) || new Set();
          eidSet.add(capturable);
          frameToEids.set(frame, eidSet);

          // snapToFrame(world, frame, capturable);
        }
      });
    }

    // Handle grabbing logic with timeout
    if (objeid) {
      if (releaseTimeout) {
        clearTimeout(releaseTimeout);
        releaseTimeout = null;
      }
    }

    display(world, frame, objeid);
  }
}
